/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { ReactNode } from "react";

// @mui material components
import Icon from "@mui/material/Icon";
import { Theme } from "@mui/material/styles";

// Material Dashboard 2 PRO React TS components
import MDBox from "dashboard-template/components/MDBox";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController } from "context/ThemeContext";

// Declaring props types for DataTableHeadCell
interface Props {
  width?: string | number;
  children: ReactNode;
  sorted?: false | "none" | "asce" | "desc";
  align?: "left" | "right" | "center";
  sortIndicatorType?: "az" | "numeric";
}

function DataTableHeadCell({
  width = "auto",
  children,
  sorted = "none",
  align = "left",
  sortIndicatorType = "az",
  ...rest
}: Readonly<Props>): JSX.Element {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Helper function to get descriptive text for text and numeric columns
  const getDescriptiveText = () => {
    if (sorted && sorted !== "none") {
      if (sortIndicatorType === "az") {
        return {
          top: sorted === "asce" ? "A" : "Z",
          bottom: sorted === "asce" ? "Z" : "A",
        };
      }
      if (sortIndicatorType === "numeric") {
        return {
          top: sorted === "asce" ? "1" : "9",
          bottom: sorted === "asce" ? "9" : "1",
        };
      }
    }
    return null;
  };

  const descriptiveText = getDescriptiveText();

  return (
    <MDBox
      component="th"
      width={width}
      py={1.5}
      px={3}
      sx={({ palette: { light }, borders: { borderWidth } }: Theme) => ({
        borderBottom: `${borderWidth[1]} solid ${light.main}`,
      })}
    >
      <MDBox
        {...rest}
        position="relative"
        textAlign={align}
        color={darkMode ? "white" : "secondary"}
        opacity={0.7}
        sx={({ typography: { size, fontWeightBold } }: Theme) => ({
          fontSize: size.xxs,
          fontWeight: fontWeightBold,
          textTransform: "uppercase",
          cursor: sorted && "pointer",
          userSelect: sorted && "none",
          paddingRight: sorted ? 3 : 0,
        })}
      >
        {children}
        {sorted && (
          <MDBox
            position="absolute"
            top={0}
            right={align !== "right" ? "16px" : 0}
            left={align === "right" ? "-5px" : "unset"}
            sx={{
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            {descriptiveText && (
              <MDBox
                sx={{
                  fontSize: 8,
                  fontWeight: "bold",
                  color: darkMode ? "#fff" : "#000",
                  userSelect: "none",
                  lineHeight: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <span>{descriptiveText.top}</span>
                <span>{descriptiveText.bottom}</span>
              </MDBox>
            )}
            <MDBox
              sx={({ typography: { size } }: any) => ({
                fontSize: size.lg,
                position: "relative",
                alignSelf: "flex-start",
                marginTop: "2px",
              })}
            >
              <MDBox
                position="absolute"
                top={-6}
                color={sorted === "asce" ? "text" : "secondary"}
                opacity={sorted === "asce" ? 1 : 0.5}
              >
                <Icon>arrow_drop_up</Icon>
              </MDBox>
              <MDBox
                position="absolute"
                top={0}
                color={sorted === "desc" ? "text" : "secondary"}
                opacity={sorted === "desc" ? 1 : 0.5}
              >
                <Icon>arrow_drop_down</Icon>
              </MDBox>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

export default DataTableHeadCell;
